const REVEAL_SELECTOR = '[data-scroll-reveal]';
const GRID_SELECTOR = '[data-scroll-reveal-grid]';
const DISABLED_SELECTOR = '[data-scroll-reveal-disabled]';
const GRID_DELAY_STEP = 200;
const ROW_TOLERANCE = 4;
const THRESHOLD = 0.2;
const STACKED_VIEWPORT_QUERY = '(width < 48rem)';
const CARD_REVEAL_SELECTOR = '.card, .footer-cards-item';
const REVEAL_TRANSFORMS = {
    left: {
        x: '-1.5rem',
        y: '0',
    },
    right: {
        x: '1.5rem',
        y: '0',
    },
    up: {
        x: '0',
        y: '1.5rem',
    },
};
const RICH_TEXT_IMAGE_SELECTOR = [
    '.text-rich figure.image-style-align-left:not([data-scroll-reveal])',
    '.text-rich figure.image-style-block-align-left:not([data-scroll-reveal])',
    '.text-rich figure.image-style-align-right:not([data-scroll-reveal])',
    '.text-rich figure.image-style-block-align-right:not([data-scroll-reveal])',
    '.text-rich figure.image-style-align-center:not([data-scroll-reveal])',
    '.text-rich figure.image-style-block-align-center:not([data-scroll-reveal])',
    '.text-rich figure.image:not([data-scroll-reveal])',
].join(',');

const getRevealDirection = (element) => {
    if (element.classList.contains('image-style-align-left') || element.classList.contains('image-style-block-align-left')) {
        return 'left';
    }

    if (element.classList.contains('image-style-align-right') || element.classList.contains('image-style-block-align-right')) {
        return 'right';
    }

    if (element.classList.contains('image-style-align-center') || element.classList.contains('image-style-block-align-center')) {
        return 'up';
    }

    const revealContext = element.closest('[data-scroll-reveal-direction]');

    if (revealContext?.dataset.scrollRevealDirection) {
        return revealContext.dataset.scrollRevealDirection;
    }

    return 'up';
};

const applyRevealDirection = (element, direction) => {
    const baseDirection = REVEAL_TRANSFORMS[direction] ? direction : 'up';
    const revealDirection = window.matchMedia(STACKED_VIEWPORT_QUERY).matches ? 'up' : baseDirection;
    const transform = {
        ...REVEAL_TRANSFORMS[revealDirection],
    };

    if (revealDirection === 'up' && element.matches(CARD_REVEAL_SELECTOR)) {
        transform.y = '1rem';
    }

    element.dataset.scrollReveal = baseDirection;
    element.style.setProperty('--scroll-reveal-x', transform.x);
    element.style.setProperty('--scroll-reveal-y', transform.y);
};

const findElements = (root, selector) => {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    const elements = scope.matches?.(selector) ? [scope] : [];

    return elements.concat(Array.from(scope.querySelectorAll(selector)));
};

const getGridItemDelay = (columnIndex) => columnIndex * GRID_DELAY_STEP;

const isRevealDisabled = (element) => Boolean(element.closest(DISABLED_SELECTOR));

const scrollReveal = {
    observer: null,
    resizeTimeout: null,

    reveal(element) {
        element.classList.add('is-visible');
    },

    canAnimate() {
        return 'IntersectionObserver' in window
            && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    init() {
        if (!this.canAnimate()) {
            return;
        }

        window.clearTimeout(window.__heiScrollRevealFallback);
        document.documentElement.classList.remove('scroll-reveal-fallback');
        document.documentElement.classList.add('scroll-reveal-enabled');

        this.observer = new IntersectionObserver((entries, observer) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                this.reveal(entry.target);
                observer.unobserve(entry.target);
            });
        }, {
            rootMargin: '0px',
            threshold: THRESHOLD,
        });

        this.refresh();

        window.addEventListener('resize', () => {
            window.clearTimeout(this.resizeTimeout);

            this.resizeTimeout = window.setTimeout(() => {
                this.updateRevealDirections();
                this.updateGridDelays();
            }, 150);
        });
    },

    refresh(root = document) {
        if (!this.observer || !this.canAnimate()) {
            return;
        }

        findElements(root, RICH_TEXT_IMAGE_SELECTOR).forEach((element) => {
            if (isRevealDisabled(element)) {
                return;
            }

            applyRevealDirection(element, getRevealDirection(element));
        });

        this.updateGridDelays(root);

        findElements(root, REVEAL_SELECTOR).forEach((element) => {
            if (isRevealDisabled(element)) {
                return;
            }

            if (element.dataset.scrollRevealInit === 'true') {
                return;
            }

            applyRevealDirection(element, element.dataset.scrollReveal);
            element.dataset.scrollRevealInit = 'true';
            this.observer.observe(element);
        });
    },

    updateGridDelays(root = document) {
        findElements(root, GRID_SELECTOR).forEach((grid) => {
            if (isRevealDisabled(grid)) {
                return;
            }

            let currentRowTop = null;
            let columnIndex = 0;

            Array.from(grid.children)
                .filter((element) => element.matches(REVEAL_SELECTOR))
                .forEach((element) => {
                    const top = element.getBoundingClientRect().top;
                    const isNewRow = currentRowTop === null || Math.abs(top - currentRowTop) > ROW_TOLERANCE;

                    if (isNewRow) {
                        currentRowTop = top;
                        columnIndex = 0;
                    }

                    element.style.setProperty('--scroll-reveal-delay', `${getGridItemDelay(columnIndex)}ms`);
                    columnIndex += 1;
                });
        });
    },

    updateRevealDirections(root = document) {
        findElements(root, REVEAL_SELECTOR).forEach((element) => {
            if (isRevealDisabled(element)) {
                return;
            }

            applyRevealDirection(element, element.dataset.scrollReveal);
        });
    },
};

export default scrollReveal;
